import { Box, Group, Text, Stack } from "@mantine/core"
import { useMemo, useEffect, useRef } from "react"
import styles from "./PasswordStrength.module.css"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { AEMImage } from "@components/AEMImage"
import { useAEMIcons } from "@AEM/hooks/useAEMIcons"
import { getPasswordValidationDetails } from "@utils/password.utils"
import { useIsMobile } from "@utils/device.utils"

interface PasswordStrengthProps {
	password: string
	confirmPassword?: string
	showRequirements?: boolean
	userCannotRepeatPassword?: boolean
	isPasswordRepeated?: boolean
	passwordFieldId?: string
}

export function PasswordStrength({
	password,
	confirmPassword,
	showRequirements = false,
	userCannotRepeatPassword = false,
	isPasswordRepeated = false,
	passwordFieldId = "password-field"
}: PasswordStrengthProps) {
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const liveRegionRef = useRef<HTMLDivElement>(null)

	// Generate unique IDs for requirements
	const requirementsId = `${passwordFieldId}-requirements`
	const liveRegionId = `${passwordFieldId}-live-region`

	const requirements = useMemo(() => {
		const validation = getPasswordValidationDetails(password, confirmPassword)
		const hasLongerLength = password.length > 12

		return {
			hasValidLength: validation.hasValidLength,
			hasLongerLength,
			hasNumber: validation.hasNumber,
			hasUpperCase: validation.hasUpperCase,
			hasLowerCase: validation.hasLowerCase,
			hasSpecialChar: validation.hasSpecialChar,
			isPasswordMatch: validation.passwordsMatch,
			...(userCannotRepeatPassword && {
				userCannotRepeatPassword: isPasswordRepeated
			})
		}
	}, [password, confirmPassword])

	const strengthScore = useMemo(() => {
		let score = 0

		// 1. Length between 9-15 characters
		if (requirements.hasValidLength) score++

		// 2. Contains at least one digit
		if (requirements.hasNumber) score++

		// 3. Contains both uppercase and lowercase
		if (requirements.hasUpperCase && requirements.hasLowerCase) score++

		// 4. Contains special character
		if (requirements.hasSpecialChar) score++

		// 5. Length > 12 characters (bonus point)
		if (requirements.hasLongerLength) score++

		return score
	}, [requirements])

	const progressBarLabel = dictionary?.progressBarLabel
	const strengthLevel = useMemo(() => {
		return `${progressBarLabel ?? ""} ${(strengthScore / 5) * 100}%`
	}, [strengthScore, progressBarLabel])

	// Announce password strength changes to screen readers
	useEffect(() => {
		if (password && liveRegionRef.current) {
			const allRequirementsMet = Object.values(requirements).every(
				(req) => req === true
			)
			const announcement = allRequirementsMet
				? `${dictionary?.passwordRequirementMustLabel || "Password requirements met"}. ${strengthLevel}`
				: `${strengthLevel}. ${Object.values(requirements).filter((req) => req === false).length} requirements remaining.`

			liveRegionRef.current.textContent = announcement
		}
	}, [password, strengthLevel, requirements, dictionary])

	const renderStrengthBars = useMemo(
		() => (
			<Group gap={4} mb={8} w="100%">
				{Array.from({ length: 5 }, (_, index) => (
					<Box
						key={index}
						className={`${styles.strengthBar} ${
							index < strengthScore ? styles.filled : styles.empty
						}`}
						style={{
							backgroundColor:
								index < strengthScore
									? "var(--mantine-color-royal-62)"
									: undefined
						}}
					/>
				))}
			</Group>
		),
		[strengthScore]
	)

	const requirementsList = useMemo(() => {
		const list = [
			{
				id: `${requirementsId}-length`,
				met: requirements.hasValidLength,
				text: dictionary?.passwordRequirement2
			},
			{
				id: `${requirementsId}-number`,
				met: requirements.hasNumber,
				text: dictionary?.passwordRequirement5
			},
			{
				id: `${requirementsId}-uppercase`,
				met: requirements.hasUpperCase,
				text: dictionary?.passwordRequirement4
			},
			{
				id: `${requirementsId}-lowercase`,
				met: requirements.hasLowerCase,
				text: dictionary?.passwordRequirement3
			},
			{
				id: `${requirementsId}-special`,
				met: requirements.hasSpecialChar,
				text: dictionary?.passwordRequirement6
			}
		]

		return list
	}, [requirements, dictionary, requirementsId])

	const { icons } = useAEMIcons()

	const successIcon = icons?.["icon_password-check.svg"]
	const idleIcon = icons?.["icon_password-idle.svg"]
	const errorIcon = icons?.["icon_password-cross.svg"]

	const getIcon = (met: boolean) => {
		let icon = errorIcon
		if (!password) icon = idleIcon
		else if (met) icon = successIcon

		return <AEMImage imageData={icon} />
	}

	const isMobile = useIsMobile()
	return (
		<>
			{/* Visually hidden aria-live region for screen reader announcements */}
			<div
				ref={liveRegionRef}
				id={liveRegionId}
				aria-live="polite"
				aria-atomic="true"
				style={{
					position: "absolute",
					left: "-10000px",
					width: "1px",
					height: "1px",
					overflow: "hidden"
				}}
			/>

			<Stack gap={isMobile ? 0 : 25} mb={24} w={300}>
				<Stack gap={16} align="center">
					{renderStrengthBars}
					<Text fz={14} fw={600} mt={8} mb={8}>
						{strengthLevel}
					</Text>
				</Stack>

				{showRequirements && (
					<Stack
						w="100%"
						gap={4}
						mt={8}
						align="center"
						ta="left"
						id={requirementsId}
					>
						<Text fz={16} fw={600} lh={1.2} mb={15}>
							{dictionary?.passwordRequirementMustLabel}
						</Text>
						<Stack w="100%" gap={8} align="start" ta="left">
							{userCannotRepeatPassword && (
								<Text fz={12} style={{ display: "flex", alignItems: "center" }}>
									<Box component="span" w={20} h={20} mr={8}>
										{isPasswordRepeated ? (
											<AEMImage imageData={errorIcon} />
										) : (
											<AEMImage imageData={idleIcon} />
										)}
									</Box>
									{dictionary?.passwordRequirement1}
								</Text>
							)}
							{requirementsList.map((requirement, index) => (
								<Box key={index}>
									<Text
										fz={12}
										style={{ display: "flex", alignItems: "center" }}
										id={requirement.id}
									>
										<Box
											component="span"
											aria-hidden="true"
											w={20}
											h={20}
											mr={8}
										>
											{getIcon(requirement?.met)}
										</Box>
										{requirement.text}
									</Text>
									{index === 0 && (
										<Text fz={12} fw={500} lh={1.6} mt={8} mb={8}>
											{dictionary?.passwordMinRequirement}
										</Text>
									)}
								</Box>
							))}
						</Stack>
					</Stack>
				)}
			</Stack>
		</>
	)
}
